import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLoginViewModel } from "../../viewmodel/useLoginViewModel";
import { useInlineLoading } from "@/shared/loading";
import { routes } from "@/app/navigation/routes";
import andyHdLogo from "@/assets/andyhdlogo.png";
import farmhouseBg from "@/assets/images/login-farmhouse-bg.jpg";
import styles from "./LoginForm.module.css";

/**
 * LoginForm Component
 * Matches exact UI mockup with split hero layout and high-conversion login card.
 * Preserves all authentication logic and bindings intact.
 */
export const LoginForm = () => {
  const location = useLocation();
  const {
    credentials,
    isLoading,
    emailError,
    passwordError,
    updateEmail,
    updatePassword,
    handleSubmit,
    handleGoogleLogin,
    handleAppleLogin,
    handleForgotPassword,
  } = useLoginViewModel();

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const BUTTON_ID = "login-button";
  const { isLoading: isInlineLoading } = useInlineLoading(BUTTON_ID);
  const buttonLoading = isLoading || isInlineLoading;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email.trim());

  return (
    <div className={styles.pageContainer} style={{ backgroundImage: `url(${farmhouseBg})` }}>
      <div className={styles.pageOverlay} />

      {/* ================================================================
          Top Navigation Bar
          ================================================================ */}
      <header className={styles.header}>
        <Link to={routes.landing} className={styles.logoWrap}>
          <img src={andyHdLogo} alt="Andy Mascot" className={styles.logoImg} />
          <span className={styles.logoText}>ANDY</span>
        </Link>

        <nav className={styles.navLinks}>
          <span className={styles.navLinkItem}>Property Tax Protection</span>
          <span className={styles.navDivider}>|</span>
          <span className={styles.navLinkItem}>Save More</span>
          <span className={styles.navDivider}>|</span>
          <span className={styles.navLinkItem}>Brighter Communities</span>
        </nav>
      </header>

      {/* ================================================================
          Main 2-Column Split Content
          ================================================================ */}
      <main className={styles.mainContent}>
        {/* Left Side: Hero Information & Features */}
        <section className={styles.heroSection}>
          <div className={styles.heroPill}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span>Property Tax Protection</span>
          </div>

          <h1 className={styles.heroTitle}>
            Save More on
            <br />
            Your{" "}
            <span className={styles.heroTitleGreenWrap}>
              <span className={styles.heroTitleGreen}>Property Taxes.</span>
              <svg
                className={styles.heroSwoosh}
                viewBox="0 0 240 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M3 14C65 4 175 4 237 12"
                  stroke="#a3e635"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h1>

          <p className={styles.heroDescription}>
            File a property tax protest, track your progress,
            <br />
            and keep more of what&apos;s yours — all in one place.
          </p>

          <div className={styles.featuresList}>
            <div className={styles.featureItem}>
              <div className={styles.featureIconCircle}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div className={styles.featureText}>
                <h4 className={styles.featureTitle}>File Easily</h4>
                <p className={styles.featureDesc}>Submit your protest in minutes.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureIconCircle}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <div className={styles.featureText}>
                <h4 className={styles.featureTitle}>Maximize Savings</h4>
                <p className={styles.featureDesc}>Potentially reduce your property taxes.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureIconCircle}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </div>
              <div className={styles.featureText}>
                <h4 className={styles.featureTitle}>Track Progress</h4>
                <p className={styles.featureDesc}>Stay updated at every step.</p>
              </div>
            </div>
          </div>

          <div className={styles.statsCard}>
            <div className={styles.statCol}>
              <div className={styles.statIconCircle}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className={styles.statText}>
                <span className={styles.statValue}>10K+</span>
                <span className={styles.statLabel}>Homeowners Trust Andy</span>
              </div>
            </div>

            <div className={styles.statDivider} />

            <div className={styles.statCol}>
              <div className={styles.statIconCircle}>
                <span className={styles.statDollarText}>$</span>
              </div>
              <div className={styles.statText}>
                <span className={styles.statValue}>$3.2M+</span>
                <span className={styles.statLabel}>Potential Savings for Our Users</span>
              </div>
            </div>

            <div className={styles.statDivider} />

            <div className={styles.statCol}>
              <div className={styles.statIconCircle}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <div className={styles.statText}>
                <span className={styles.statValue}>98%</span>
                <span className={styles.statLabel}>Success Rate</span>
              </div>
            </div>
          </div>
        </section>

        {/* Right Side: Login Card */}
        <section className={styles.loginCardWrapper}>
          <div className={styles.loginCard}>
            <h2 className={styles.cardTitle}>
              Welcome <span className={styles.cardTitleGreen}>back</span>
            </h2>
            <p className={styles.cardSubtitle}>
              Enter your details to get sign in to your account.
            </p>

            <form className={styles.form} onSubmit={handleSubmit}>
              {/* Email Input */}
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="email">
                  Email
                </label>
                <div className={`${styles.inputBox} ${emailError ? styles.inputBoxError : ""}`}>
                  <span className={styles.inputIcon}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    value={credentials.email}
                    onChange={(e) => updateEmail(e.target.value)}
                    placeholder="andy@gmail.com"
                  />
                  {isEmailValid ? (
                    <span className={styles.emailValidIcon} aria-label="Valid email format">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="#16a34a" />
                        <polyline
                          points="8 12 11 15 16 9"
                          stroke="#ffffff"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  ) : (
                    credentials.email && (
                      <button
                        type="button"
                        className={styles.clearInputBtn}
                        onClick={() => updateEmail("")}
                        aria-label="Clear email input"
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )
                  )}
                </div>
                {emailError && <div className={styles.errorMessage}>{emailError}</div>}
              </div>

              {/* Password Input */}
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="password">
                  Password
                </label>
                <div className={`${styles.inputBox} ${passwordError ? styles.inputBoxError : ""}`}>
                  <span className={styles.inputIcon}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className={styles.input}
                    value={credentials.password}
                    onChange={(e) => updatePassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleForgotPassword}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleForgotPassword();
                    }
                  }}
                  className={styles.forgotPasswordLink}
                >
                  Forgot Password?
                </span>
              </div>

              {/* Continue Button */}
              <button
                id={BUTTON_ID}
                type="submit"
                className={styles.continueButton}
                disabled={buttonLoading}
              >
                {buttonLoading ? (
                  "Loading..."
                ) : (
                  <>
                    <span>Continue</span>
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Separator */}
            <div className={styles.separator}>
              <div className={styles.separatorLine} />
              <span className={styles.separatorText}>Or Sign in with</span>
              <div className={styles.separatorLine} />
            </div>

            {/* Social Buttons */}
            <div className={styles.socialButtons}>
              <button
                id="with-google-button"
                type="button"
                className={styles.socialButton}
                onClick={handleGoogleLogin}
              >
                <div className={styles.socialIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                    />
                  </svg>
                </div>
                <span>Google</span>
              </button>

              <button
                id="with-apple-button"
                type="button"
                className={styles.socialButton}
                onClick={handleAppleLogin}
              >
                <div className={styles.socialIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.92-2.85-.9.04-1.99.6-2.64 1.35-.56.65-1.06 1.71-.93 2.74 1.01.08 2.03-.5 2.65-1.24z" />
                  </svg>
                </div>
                <span>Apple</span>
              </button>
            </div>

            {/* Register Link */}
            <div className={styles.registerLink}>
              Don&apos;t have an account?
              <Link to={routes.signup} state={location.state} className={styles.registerLinkText}>
                Register Now
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
